/**
 * The temple editor's research tabs (CSR-123; content 04 §8, doc 03 §9 and §13): the pujas a
 * temple is a venue for (gate T6), the sources behind its facts (gate T4), its seasonal closures
 * (gate T7), and the verification state with the research fields that feed it.
 *
 * **Built from the spec, not a design.** The temple drawer in `design/admin-console` has no frame
 * for any of these; the owner ruled on 2026-10-26 that design-less screens are built from the docs
 * with the console's own components and restyled later.
 *
 * Every list is a whole replace, staged here and sent on Save. The client checks only what it can
 * know (a blank field, a key's shape, a date in the future); the server's 422 names the field it
 * refused — `rituals[1].note`, `sources[0].observed_on` — and is shown as it came back, which is
 * also how a content-lint refusal of public copy reaches the operator.
 */
import { useState } from 'react';
import { Button, Note, StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { EmptyState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { usePujas } from '../lib/api/hooks/catalogue';
import {
  usePatchTemple,
  useReplaceTempleClosures,
  useReplaceTempleRituals,
  useReplaceTempleSources,
  useSetTempleVerification,
  type TempleDetail,
  type TempleVerificationState,
} from '../lib/api/hooks/temples';

/** Closed sets on the server (`app/models/enums.py`); the labels are what an operator reads. */
export const RELEVANCE: Record<string, string> = {
  performed_here: 'Performed here',
  traditionally_associated: 'Traditionally associated',
  pilgrimage_only: 'Pilgrimage only',
};
export const SOURCE_KINDS = [
  'wikipedia', 'wikidata', 'temple_official', 'government', 'asi', 'hrce', 'news', 'scholarly', 'field_visit', 'trust_claim', 'other',
] as const;
export const CONFIDENCE = ['low', 'medium', 'high'] as const;
export const CLOSURE_PATTERNS: Record<string, string> = {
  none: 'Open all year',
  seasonal_winter: 'Closes for winter',
  seasonal_monsoon: 'Closes for the monsoon',
  restricted_entry: 'Restricted entry',
  under_restoration: 'Under restoration',
};
export const DATE_STATUSES = ['expected', 'announced', 'confirmed', 'passed'] as const;

/** `_VERIFICATION_MOVES` in `app/services/temples.py`: rejected is final. */
export const VERIFICATION_MOVES: Record<string, readonly TempleVerificationState[]> = {
  unverified: ['sourced', 'disputed', 'rejected'],
  sourced: ['unverified', 'verified', 'disputed', 'rejected'],
  verified: ['sourced', 'disputed', 'rejected'],
  disputed: ['sourced', 'rejected'],
  rejected: [],
};

const FACT_KEY = /^[a-z][a-z0-9_]{0,63}$/;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const NOTE_STYLE = { color: 'var(--c-text-muted)', fontSize: 'var(--fs-admin-colhead)' } as const;
const ROW_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 'var(--sp-sm)',
  alignItems: 'end',
  paddingBottom: 'var(--sp-sm)',
  borderBottom: '1px solid var(--c-border)',
} as const;

/** A linked puja the list does not carry (archived, or past the page) still shows, by its id. */
const withCurrent = (names: string[], current: string) => (current && !names.includes(current) ? [current, ...names] : names);
const orNull = (s: string | null | undefined) => (s && s.trim() ? s.trim() : null);
const keyOf = (label: string, map: Record<string, string>) => Object.keys(map).find((k) => map[k] === label)!;

/** Today in IST, as the server dates a source (`observed_on` may not be after it). */
export function istToday(now: Date = new Date()): string {
  return new Date(now.getTime() + 330 * 60_000).toISOString().slice(0, 10);
}

function Problems({ items }: { items: readonly string[] }) {
  if (items.length === 0) return null;
  return (
    <ul aria-label="Fix before saving" style={{ margin: 0, color: 'var(--c-warn-text)' }}>
      {items.map((p) => (
        <li key={p}>{p}</li>
      ))}
    </ul>
  );
}

function useSave() {
  const { scope, toast } = useConsole();
  const fail = (e: unknown) => toast({ tone: 'error', message: errorMessage(e) });
  const ok = (message: string) => () => toast({ tone: 'success', message });
  return { scope, fail, ok };
}

/* ── Pujas performed here (gate T6) ─────────────────────────────────────────────────────── */

type RitualDraft = { puja_id: number | null; relevance: string; note: string };

export function ritualProblems(rows: readonly RitualDraft[]): string[] {
  const out: string[] = [];
  const seen = new Set<number>();
  rows.forEach((r, i) => {
    if (r.puja_id === null) out.push(`Row ${i + 1} needs a puja.`);
    else if (seen.has(r.puja_id)) out.push(`Row ${i + 1} repeats a puja already listed.`);
    else seen.add(r.puja_id);
    if (r.note.trim().length > 300) out.push(`Row ${i + 1}'s note is over 300 characters.`);
  });
  return out;
}

function RitualsEditor({ detail }: { detail: TempleDetail }) {
  const { scope, fail, ok } = useSave();
  const replace = useReplaceTempleRituals();
  const pujas = usePujas({ page: 1, page_size: 200 });
  const options = pujas.data?.items ?? [];
  const nameOf = (id: number | null) => options.find((p) => p.id === id)?.name_en ?? (id === null ? '' : `Puja #${id}`);
  const source: RitualDraft[] = [...detail.rituals]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((r) => ({ puja_id: r.puja_id, relevance: r.relevance, note: r.note ?? '' }));
  const [rows, setRows] = useState<RitualDraft[]>(source);
  const dirty = JSON.stringify(rows) !== JSON.stringify(source);
  const problems = ritualProblems(rows);
  const update = (i: number, patch: Partial<RitualDraft>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <Section title="Pujas performed here" note="Publishing needs at least one — the temple is bookable as a venue">
      <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
        <Note>The note is public copy on the temple page, so it is content-linted when saved.</Note>
        {rows.length === 0 && <EmptyState icon="book-open" title="No pujas linked" body="Gate T6 holds the temple back from publishing until one is." inline />}
        {rows.map((row, i) => (
          <div key={i} style={ROW_STYLE}>
            <Select
              density="admin"
              label={`Puja, row ${i + 1}`}
              placeholder={pujas.isPending ? 'Loading pujas…' : 'Choose a puja'}
              options={withCurrent(options.map((p) => p.name_en), nameOf(row.puja_id))}
              value={nameOf(row.puja_id) || undefined}
              onChange={(v) => update(i, { puja_id: options.find((p) => p.name_en === v)!.id })}
            />
            <Select
              density="admin"
              label={`Relevance, row ${i + 1}`}
              options={Object.values(RELEVANCE)}
              value={RELEVANCE[row.relevance] ?? row.relevance}
              onChange={(v) => update(i, { relevance: keyOf(v, RELEVANCE) })}
            />
            <Input density="admin" label={`Note, row ${i + 1}`} value={row.note} onChange={(v) => update(i, { note: v })} />
            <Button size="sm" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
              Remove row {i + 1}
            </Button>
          </div>
        ))}
        <Problems items={problems} />
        <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
          <Button size="sm" variant="secondary" onClick={() => setRows([...rows, { puja_id: null, relevance: 'performed_here', note: '' }])}>
            Link a puja
          </Button>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            disabled={!dirty || problems.length > 0}
            loading={replace.isPending}
            onClick={() =>
              replace.mutate(
                {
                  templeId: detail.id,
                  items: rows.map((r, i) => ({ puja_id: r.puja_id!, relevance: r.relevance, note: orNull(r.note), sort_order: i })),
                },
                { onSuccess: ok('Pujas saved'), onError: fail },
              )
            }
          >
            Save pujas
          </ScopeAction>
        </div>
      </div>
    </Section>
  );
}

export function TempleRituals({ detail }: { detail: TempleDetail }) {
  return <RitualsEditor key={JSON.stringify(detail.rituals)} detail={detail} />;
}

/* ── Sources (gate T4) ──────────────────────────────────────────────────────────────────── */

type SourceDraft = { fact_key: string; source_kind: string; source_ref: string; observed_on: string; confidence: string; note: string };

export function sourceProblems(rows: readonly SourceDraft[], today: string): string[] {
  const out: string[] = [];
  rows.forEach((r, i) => {
    const n = i + 1;
    if (!FACT_KEY.test(r.fact_key)) out.push(`Source ${n}: the fact is snake_case, like location or founded_era.`);
    if (!r.source_ref.trim()) out.push(`Source ${n} needs a reference.`);
    else if (r.source_ref.trim().length > 500) out.push(`Source ${n}'s reference is over 500 characters.`);
    if (!ISO_DAY.test(r.observed_on)) out.push(`Source ${n} needs the date it was read, as YYYY-MM-DD.`);
    else if (r.observed_on > today) out.push(`Source ${n} is dated in the future; date it when it was read.`);
    if (r.note.trim().length > 400) out.push(`Source ${n}'s note is over 400 characters.`);
  });
  return out;
}

function SourcesEditor({ detail, today }: { detail: TempleDetail; today: string }) {
  const { scope, fail, ok } = useSave();
  const replace = useReplaceTempleSources();
  const source: SourceDraft[] = detail.sources.map((s) => ({
    fact_key: s.fact_key,
    source_kind: s.source_kind,
    source_ref: s.source_ref,
    observed_on: s.observed_on,
    confidence: s.confidence,
    note: s.note ?? '',
  }));
  const [rows, setRows] = useState<SourceDraft[]>(source);
  const dirty = JSON.stringify(rows) !== JSON.stringify(source);
  const problems = sourceProblems(rows, today);
  const update = (i: number, patch: Partial<SourceDraft>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <Section title="Sources" note="Internal — never rendered on the page">
      <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
        <Note>One row per fact and where it was read. Publishing needs at least one source for the page&apos;s facts.</Note>
        {rows.length === 0 && <EmptyState icon="file-check" title="No sources recorded" body="Gate T4 holds the temple back from publishing until one is." inline />}
        {rows.map((row, i) => (
          <div key={i} style={ROW_STYLE}>
            <Input density="admin" label={`Fact, source ${i + 1}`} value={row.fact_key} placeholder="founded_era" onChange={(v) => update(i, { fact_key: v })} />
            <Select density="admin" label={`Kind, source ${i + 1}`} options={SOURCE_KINDS} value={row.source_kind} onChange={(v) => update(i, { source_kind: v })} />
            <Input density="admin" label={`Reference, source ${i + 1}`} value={row.source_ref} placeholder="A link, or a book and page" onChange={(v) => update(i, { source_ref: v })} />
            <Input density="admin" placeholder="YYYY-MM-DD" label={`Read on, source ${i + 1}`} value={row.observed_on} onChange={(v) => update(i, { observed_on: v })} />
            <Select density="admin" label={`Confidence, source ${i + 1}`} options={CONFIDENCE} value={row.confidence} onChange={(v) => update(i, { confidence: v })} />
            <Input density="admin" label={`Note, source ${i + 1}`} value={row.note} onChange={(v) => update(i, { note: v })} />
            <Button size="sm" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
              Remove source {i + 1}
            </Button>
          </div>
        ))}
        <Problems items={problems} />
        <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setRows([...rows, { fact_key: '', source_kind: 'temple_official', source_ref: '', observed_on: today, confidence: 'medium', note: '' }])}
          >
            Add a source
          </Button>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            disabled={!dirty || problems.length > 0}
            loading={replace.isPending}
            onClick={() =>
              replace.mutate(
                { templeId: detail.id, items: rows.map((r) => ({ ...r, source_ref: r.source_ref.trim(), note: orNull(r.note) })) },
                { onSuccess: ok('Sources saved'), onError: fail },
              )
            }
          >
            Save sources
          </ScopeAction>
        </div>
      </div>
    </Section>
  );
}

export function TempleSources({ detail, now }: { detail: TempleDetail; now?: Date }) {
  return <SourcesEditor key={JSON.stringify(detail.sources)} detail={detail} today={istToday(now)} />;
}

/* ── Seasonal closure (gate T7) ─────────────────────────────────────────────────────────── */

type ClosureDraft = {
  season_year: string;
  opens_on: string;
  opens_on_status: string;
  closes_on: string;
  closes_on_status: string;
  announcement_note: string;
  source_ref: string;
  winter_seat_note: string;
};

export function closureProblems(rows: readonly ClosureDraft[]): string[] {
  const out: string[] = [];
  const years = new Set<string>();
  rows.forEach((r, i) => {
    const n = i + 1;
    const year = Number(r.season_year);
    if (!/^\d{4}$/.test(r.season_year) || year < 2000 || year > 2100) out.push(`Season ${n} needs a year from 2000 to 2100.`);
    else if (years.has(r.season_year)) out.push(`Season ${n} repeats the year ${r.season_year}.`);
    else years.add(r.season_year);
    for (const [day, what] of [[r.opens_on, 'opening'], [r.closes_on, 'closing']] as const) {
      if (!day) continue;
      if (!ISO_DAY.test(day)) out.push(`Season ${n}'s ${what} date is YYYY-MM-DD or blank.`);
      else if (day.slice(0, 4) !== r.season_year) out.push(`Season ${n}'s ${what} date is not in ${r.season_year || 'its year'}.`);
    }
    if (ISO_DAY.test(r.opens_on) && ISO_DAY.test(r.closes_on) && r.closes_on < r.opens_on) out.push(`Season ${n} closes before it opens.`);
    if (r.announcement_note.trim().length > 300) out.push(`Season ${n}'s announcement is over 300 characters.`);
    if (r.winter_seat_note.trim().length > 300) out.push(`Season ${n}'s winter-seat note is over 300 characters.`);
    if (r.source_ref.trim().length > 500) out.push(`Season ${n}'s source is over 500 characters.`);
  });
  return out;
}

function ClosuresEditor({ detail }: { detail: TempleDetail }) {
  const { scope, fail, ok } = useSave();
  const replace = useReplaceTempleClosures();
  const patch = usePatchTemple();
  const [pattern, setPattern] = useState(detail.closure_pattern);
  const source: ClosureDraft[] = detail.closures.map((c) => ({
    season_year: String(c.season_year),
    opens_on: c.opens_on ?? '',
    opens_on_status: c.opens_on_status,
    closes_on: c.closes_on ?? '',
    closes_on_status: c.closes_on_status,
    announcement_note: c.announcement_note ?? '',
    source_ref: c.source_ref ?? '',
    winter_seat_note: c.winter_seat_note ?? '',
  }));
  const [rows, setRows] = useState<ClosureDraft[]>(source);
  const dirty = JSON.stringify(rows) !== JSON.stringify(source);
  const problems = closureProblems(rows);
  const update = (i: number, p: Partial<ClosureDraft>) => setRows(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));

  return (
    <Section title="Seasonal closure" note="Dates carry their own status — expected is not announced">
      <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'end' }}>
          <Select
            density="admin"
            label="Closure pattern"
            options={Object.values(CLOSURE_PATTERNS)}
            value={CLOSURE_PATTERNS[pattern] ?? pattern}
            onChange={(v) => setPattern(keyOf(v, CLOSURE_PATTERNS))}
          />
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            disabled={pattern === detail.closure_pattern}
            loading={patch.isPending}
            onClick={() =>
              patch.mutate(
                { templeId: detail.id, body: { closure_pattern: pattern as 'none' } },
                { onSuccess: ok('Closure pattern saved'), onError: fail },
              )
            }
          >
            Save pattern
          </ScopeAction>
        </div>
        <Note>Both notes are public copy and are content-linted when saved.</Note>
        {rows.length === 0 && <span style={NOTE_STYLE}>No seasons recorded.</span>}
        {rows.map((row, i) => (
          <div key={i} style={ROW_STYLE}>
            <Input density="admin" label={`Year, season ${i + 1}`} value={row.season_year} inputMode="numeric" onChange={(v) => update(i, { season_year: v })} />
            <Input density="admin" placeholder="YYYY-MM-DD" label={`Opens on, season ${i + 1}`} value={row.opens_on} onChange={(v) => update(i, { opens_on: v })} />
            <Select density="admin" label={`Opening status, season ${i + 1}`} options={DATE_STATUSES} value={row.opens_on_status} onChange={(v) => update(i, { opens_on_status: v })} />
            <Input density="admin" placeholder="YYYY-MM-DD" label={`Closes on, season ${i + 1}`} value={row.closes_on} onChange={(v) => update(i, { closes_on: v })} />
            <Select density="admin" label={`Closing status, season ${i + 1}`} options={DATE_STATUSES} value={row.closes_on_status} onChange={(v) => update(i, { closes_on_status: v })} />
            <Input density="admin" label={`Announcement, season ${i + 1}`} value={row.announcement_note} onChange={(v) => update(i, { announcement_note: v })} />
            <Input density="admin" label={`Winter seat, season ${i + 1}`} value={row.winter_seat_note} onChange={(v) => update(i, { winter_seat_note: v })} />
            <Input density="admin" label={`Source, season ${i + 1}`} value={row.source_ref} onChange={(v) => update(i, { source_ref: v })} />
            <Button size="sm" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
              Remove season {i + 1}
            </Button>
          </div>
        ))}
        <Problems items={problems} />
        <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              setRows([
                ...rows,
                { season_year: '', opens_on: '', opens_on_status: 'expected', closes_on: '', closes_on_status: 'expected', announcement_note: '', source_ref: '', winter_seat_note: '' },
              ])
            }
          >
            Add a season
          </Button>
          <ScopeAction
            scope={scope}
            need="catalogue_content"
            size="sm"
            disabled={!dirty || problems.length > 0}
            loading={replace.isPending}
            onClick={() =>
              replace.mutate(
                {
                  templeId: detail.id,
                  items: rows.map((r) => ({
                    season_year: Number(r.season_year),
                    opens_on: r.opens_on || null,
                    opens_on_status: r.opens_on_status,
                    closes_on: r.closes_on || null,
                    closes_on_status: r.closes_on_status,
                    announcement_note: orNull(r.announcement_note),
                    source_ref: orNull(r.source_ref),
                    winter_seat_note: orNull(r.winter_seat_note),
                  })),
                },
                { onSuccess: ok('Seasons saved'), onError: fail },
              )
            }
          >
            Save seasons
          </ScopeAction>
        </div>
      </div>
    </Section>
  );
}

export function TempleClosures({ detail }: { detail: TempleDetail }) {
  return <ClosuresEditor key={`${detail.closure_pattern}:${JSON.stringify(detail.closures)}`} detail={detail} />;
}

/* ── Verification and research fields (doc 03 §9, §13) ──────────────────────────────────── */

const VERIFICATION_TONE: Record<string, 'success' | 'warn' | 'neutral' | 'error'> = {
  verified: 'success',
  sourced: 'warn',
  disputed: 'error',
  rejected: 'error',
};

type ResearchDraft = {
  district_current_id: string;
  district_legacy_id: string;
  wikidata_id: string;
  wikipedia_title: string;
  footfall_note: string;
  demand_confidence: 'low' | 'normal';
  vernacular_demand_override: string;
  vernacular_demand_note: string;
};

/* 12-content/03 §8.1: English views under-measure the Hindi belt, so `low` is the default and
   `normal` is a claim an editor makes about who the temple's devotees are. */
const DEMAND_CONFIDENCE = {
  'Low — devotees are not English-dominant': 'low',
  'Normal — devotees are English-dominant': 'normal',
} as const;

export function researchProblems(d: ResearchDraft): string[] {
  const out: string[] = [];
  if (d.district_current_id && !/^\d+$/.test(d.district_current_id)) out.push('The current district is a district id, digits only.');
  if (d.district_legacy_id && !/^\d+$/.test(d.district_legacy_id)) out.push('The legacy district is a district id, digits only.');
  if (d.wikidata_id && !/^Q\d+$/.test(d.wikidata_id)) out.push('A Wikidata id is Q and digits, like Q1234.');
  if (d.vernacular_demand_override && !/^\d+$/.test(d.vernacular_demand_override)) out.push('The demand override is a 12-month count, digits only.');
  if (d.vernacular_demand_override && !d.vernacular_demand_note.trim()) out.push('An override needs a note saying what it rests on.');
  return out;
}

const idOrNull = (s: string) => (s ? Number(s) : null);

function VerificationPanel({ detail }: { detail: TempleDetail }) {
  const { scope, fail, ok } = useSave();
  const move = useSetTempleVerification();
  const patch = usePatchTemple();
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const moves = VERIFICATION_MOVES[detail.verification_state] ?? [];
  const source: ResearchDraft = {
    district_current_id: detail.district_current_id == null ? '' : String(detail.district_current_id),
    district_legacy_id: detail.district_legacy_id == null ? '' : String(detail.district_legacy_id),
    wikidata_id: detail.wikidata_id ?? '',
    wikipedia_title: detail.wikipedia_title ?? '',
    footfall_note: detail.footfall_note ?? '',
    demand_confidence: detail.demand_confidence ?? 'low',
    vernacular_demand_override: detail.vernacular_demand_override == null ? '' : String(detail.vernacular_demand_override),
    vernacular_demand_note: detail.vernacular_demand_note ?? '',
  };
  const [draft, setDraft] = useState<ResearchDraft>(source);
  const dirty = JSON.stringify(draft) !== JSON.stringify(source);
  const problems = researchProblems(draft);
  const go = (state: TempleVerificationState, why?: string) =>
    move.mutate({ templeId: detail.id, state, reason: why }, { onSuccess: ok(`Record moved to ${state}`), onError: fail });

  return (
    <>
      <Section title="Verification" note="Publishing lists the record until it is verified">
        <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>
            <StatusChip tone={VERIFICATION_TONE[detail.verification_state] ?? 'neutral'} label={detail.verification_state} />
            {detail.verification_state === 'rejected' && <span style={NOTE_STYLE}>Rejected is final.</span>}
          </div>
          {detail.verification_blockers.length > 0 ? (
            <ul aria-label="Open verification gates" style={{ margin: 0 }}>
              {detail.verification_blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          ) : (
            <span style={{ ...NOTE_STYLE, color: 'var(--c-success-text)' }}>✓ No verification gate is open.</span>
          )}
          {moves.length > 0 && (
            <>
              <Input density="admin" label="Reason (optional, audited)" value={reason} onChange={setReason} />
              <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
                {moves.map((state) =>
                  state === 'rejected' ? (
                    <ScopeAction key={state} scope={scope} need="catalogue_content" size="sm" variant="ghost" onClick={() => setRejecting(true)}>
                      Reject…
                    </ScopeAction>
                  ) : (
                    <ScopeAction
                      key={state}
                      scope={scope}
                      need="catalogue_content"
                      size="sm"
                      variant="secondary"
                      loading={move.isPending && move.variables?.state === state}
                      onClick={() => go(state, reason.trim() || undefined)}
                    >
                      Move to {state}
                    </ScopeAction>
                  ),
                )}
              </div>
            </>
          )}
        </div>
      </Section>
      <ConfirmAction
        open={rejecting}
        title={`Reject ${detail.name}?`}
        phrase="REJECT"
        tone="error"
        consequences={['Rejected is final: the record can never move again.', 'The temple can never be published from this record.']}
        reason
        confirmLabel="Reject"
        onCancel={() => setRejecting(false)}
        onConfirm={(why) => {
          setRejecting(false);
          go('rejected', why);
        }}
      />
      <Section title="Research fields" note="Where the record came from and how much demand it has">
        <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
          <div style={ROW_STYLE}>
            <Input density="admin" label="Current district id" value={draft.district_current_id} inputMode="numeric" onChange={(v) => setDraft({ ...draft, district_current_id: v })} />
            <Input density="admin" label="Legacy district id" value={draft.district_legacy_id} inputMode="numeric" onChange={(v) => setDraft({ ...draft, district_legacy_id: v })} />
            <Input density="admin" label="Wikidata id" value={draft.wikidata_id} placeholder="Q1234" onChange={(v) => setDraft({ ...draft, wikidata_id: v })} />
            <Input density="admin" label="Wikipedia title" value={draft.wikipedia_title} onChange={(v) => setDraft({ ...draft, wikipedia_title: v })} />
          </div>
          <Input density="admin" label="Footfall note" value={draft.footfall_note} multiline onChange={(v) => setDraft({ ...draft, footfall_note: v })} helper="Public copy — content-linted when saved." />
          <span style={NOTE_STYLE}>
            Demand: {detail.demand_views_12m == null ? 'not measured yet' : `${detail.demand_views_12m.toLocaleString('en-IN')} views in 12 months`}
            {detail.demand_measured_on ? ` · measured ${detail.demand_measured_on}` : ''}
          </span>
          <div style={ROW_STYLE}>
            <Select
              density="admin"
              label="Demand confidence"
              options={Object.keys(DEMAND_CONFIDENCE)}
              value={Object.keys(DEMAND_CONFIDENCE).find((k) => DEMAND_CONFIDENCE[k as keyof typeof DEMAND_CONFIDENCE] === draft.demand_confidence)}
              onChange={(v) => setDraft({ ...draft, demand_confidence: DEMAND_CONFIDENCE[v as keyof typeof DEMAND_CONFIDENCE] })}
            />
            <Input
              density="admin"
              label="Vernacular demand override"
              value={draft.vernacular_demand_override}
              inputMode="numeric"
              helper="A 12-month count where English metrics miss it. Replaces the measured figure in page priority; never shown publicly."
              onChange={(v) => setDraft({ ...draft, vernacular_demand_override: v })}
            />
          </div>
          <Input
            density="admin"
            label="Override evidence"
            value={draft.vernacular_demand_note}
            multiline
            helper="Required with an override: whose count it is, and when."
            onChange={(v) => setDraft({ ...draft, vernacular_demand_note: v })}
          />
          <Problems items={problems} />
          <div>
            <ScopeAction
              scope={scope}
              need="catalogue_content"
              size="sm"
              disabled={!dirty || problems.length > 0}
              loading={patch.isPending}
              onClick={() =>
                patch.mutate(
                  {
                    templeId: detail.id,
                    body: {
                      district_current_id: idOrNull(draft.district_current_id),
                      district_legacy_id: idOrNull(draft.district_legacy_id),
                      wikidata_id: orNull(draft.wikidata_id),
                      wikipedia_title: orNull(draft.wikipedia_title),
                      footfall_note: orNull(draft.footfall_note),
                      demand_confidence: draft.demand_confidence,
                      vernacular_demand_override: idOrNull(draft.vernacular_demand_override),
                      vernacular_demand_note: orNull(draft.vernacular_demand_note),
                    },
                  },
                  { onSuccess: ok('Research fields saved'), onError: fail },
                )
              }
            >
              Save research fields
            </ScopeAction>
          </div>
        </div>
      </Section>
    </>
  );
}

export function TempleVerification({ detail }: { detail: TempleDetail }) {
  const k = [detail.verification_state, detail.district_current_id, detail.district_legacy_id, detail.wikidata_id, detail.wikipedia_title, detail.footfall_note, detail.demand_confidence, detail.vernacular_demand_override, detail.vernacular_demand_note].join('|');
  return <VerificationPanel key={k} detail={detail} />;
}
